import type { LookalikePersona } from '@repo/contracts';

import { SAVVINESS_SCORE } from './features';

interface Candidate {
  strength: number;
  text: string;
}

const CATEGORICAL_FIELDS = [
  'location',
  'jobTitle',
  'companySize',
  'goal',
  'painPoint',
] as const;

/** How much more common a cluster's most common value has to be than that
 *  value's share of the whole population before it's worth naming — below
 *  this, "mostly X" would overstate a trait that's really just the
 *  population's own base rate. */
const CATEGORICAL_LIFT_THRESHOLD = 0.15;

function categoricalCandidate(
  field: (typeof CATEGORICAL_FIELDS)[number],
  members: readonly LookalikePersona[],
  population: readonly LookalikePersona[],
): Candidate | undefined {
  const counts = new Map<string, number>();
  for (const member of members) {
    const value = member[field];
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  let modal: string | undefined;
  let modalCount = 0;
  for (const [value, count] of counts) {
    if (count > modalCount) {
      modal = value;
      modalCount = count;
    }
  }
  if (!modal) return undefined;

  const inClusterShare = modalCount / members.length;
  const populationShare =
    population.filter((row) => row[field] === modal).length / population.length;
  const lift = inClusterShare - populationShare;
  if (lift < CATEGORICAL_LIFT_THRESHOLD) return undefined;
  return { strength: lift, text: modal };
}

const CONTINUOUS_FIELDS = {
  age: { high: 'older', low: 'younger' },
  brandLoyalty: { high: 'more brand-loyal', low: 'less brand-loyal' },
  experienceYears: { high: 'more experienced', low: 'less experienced' },
  techSavviness: { high: 'more tech-savvy', low: 'less tech-savvy' },
} as const;

/** How many population standard deviations a cluster's mean has to sit from
 *  the population mean before it's worth naming — Cohen's convention treats
 *  0.5 as a "medium" effect, and a cluster reliably distinguished by less
 *  than that reads as noise dressed up as a trait. */
const CONTINUOUS_EFFECT_THRESHOLD = 0.5;

function numericValue(
  field: keyof typeof CONTINUOUS_FIELDS,
  row: LookalikePersona,
): number {
  if (field === 'age') return row.age;
  if (field === 'experienceYears') return row.experienceYears;
  return SAVVINESS_SCORE[row[field]];
}

function mean(values: readonly number[]): number {
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function continuousCandidate(
  field: keyof typeof CONTINUOUS_FIELDS,
  members: readonly LookalikePersona[],
  population: readonly LookalikePersona[],
): Candidate | undefined {
  const populationValues = population.map((row) => numericValue(field, row));
  const populationMean = mean(populationValues);
  const populationStd = Math.sqrt(
    mean(populationValues.map((v) => (v - populationMean) ** 2)),
  );
  if (populationStd < 1e-9) return undefined;

  const clusterMean = mean(members.map((row) => numericValue(field, row)));
  const effect = (clusterMean - populationMean) / populationStd;
  if (Math.abs(effect) < CONTINUOUS_EFFECT_THRESHOLD) return undefined;

  const phrase = CONTINUOUS_FIELDS[field][effect > 0 ? 'high' : 'low'];
  return { strength: Math.abs(effect), text: phrase };
}

/**
 * Names a cluster from what actually distinguishes it — not a fixed label
 * bank, but the two strongest traits its members share more than the
 * population at large does.
 *
 * A categorical field (location, job title, company size, goal, pain point)
 * earns a mention when its modal value is notably over-represented in this
 * cluster versus the whole population (`CATEGORICAL_LIFT_THRESHOLD`); a
 * numeric or ordinal field (age, experience, tech savviness, brand loyalty)
 * earns one when the cluster's mean sits at least a medium effect size from
 * the population mean (`CONTINUOUS_EFFECT_THRESHOLD`). Every field is checked
 * independently and the two strongest survivors win — so "Enterprise,
 * more experienced" is a claim actually backed by both fields' numbers, not a
 * hand-picked archetype.
 *
 * A cluster where nothing clears its threshold is real too — k-means assigns
 * every point somewhere, unlike density-based clustering's explicit "noise"
 * bucket, so a genuinely undifferentiated group says so rather than being
 * forced into a misleading label.
 */
export function describeCluster(
  members: readonly LookalikePersona[],
  population: readonly LookalikePersona[],
): string {
  if (members.length === 0) return 'Empty';

  const candidates = [
    ...CATEGORICAL_FIELDS.map((field) =>
      categoricalCandidate(field, members, population),
    ),
    ...(
      Object.keys(CONTINUOUS_FIELDS) as (keyof typeof CONTINUOUS_FIELDS)[]
    ).map((field) => continuousCandidate(field, members, population)),
  ].filter((candidate): candidate is Candidate => candidate !== undefined);

  candidates.sort((a, b) => b.strength - a.strength);
  const top = candidates.slice(0, 2).map((c) => c.text);
  if (top.length === 0) return 'Mixed — no single trait dominates';

  const [first, ...rest] = top;
  return [first!.charAt(0).toUpperCase() + first!.slice(1), ...rest].join(', ');
}
